import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { IconDownload } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { BaltoGPTChat } from '@/components/baltoGPT/BaltoGPTChat'
import { searchRunWithBaltoGPT, resetChatFeed } from '@/reducers/baltoGPT/baltoGPT.actions'
import { exportRunCallsCsv } from '@/reducers/insights/agenticInsights.actions'
import { EMPTY_STATE, SCOPE_ALL } from './AgenticInsights.constants'
import { buildRunBreakdown } from './AgenticInsights.runBreakdown'
import { OverviewBar } from './OverviewBar'
import { OpportunityCard } from './OpportunityCard'
import { EmptyState } from './EmptyState'

const SUGGESTED_QUESTIONS = [
  { text: 'What are my conversion opportunities?' },
  { text: 'How often are we converting?' },
  { text: 'Who are my top agents?' },
]

// How long the jumped-to opportunity card holds its highlight (ms).
const FLASH_MS = 2800

// The "No conversion opportunity" bucket: analyzed calls where no business
// outcome was on the table, so they aren't a missed conversion. Static — we
// show the real share/volume; a breakdown of *what kinds* of calls these are is
// deferred to a later version (an LLM summary over a sample of the bucket).
const NoOppCard = ({ noOpportunity }) => {
  const share = Math.round(noOpportunity?.volumeSharePct ?? 0)

  return (
    <div className="noopp-card opportunity-card" id="opportunity-noopp">
      <div className="opportunity-card__head noopp-card__head">
        <div className="opportunity-card__label">
          <div className="opportunity-card__label-text">
            <div className="opportunity-card__name">No conversion opportunity</div>
            <div className="opportunity-card__def">
              These calls had no business outcome to reach, so they aren&apos;t a missed conversion.
            </div>
          </div>
        </div>
        <div className="opportunity-card__volume">
          <div className="opportunity-card__volume-value">{share}%</div>
          <div className="opportunity-card__volume-label">of calls had no opportunity</div>
          <div className="opportunity-card__volume-count">
            ≈{(noOpportunity?.monthlyCalls ?? 0).toLocaleString()}/mo
          </div>
        </div>
      </div>
    </div>
  )
}

// Chat over the whole run. Same component as the drawer's insight chat, pointed
// at the run instead of one insight. Resets the shared chat feed when the run
// changes and on unmount so switching playbooks doesn't carry the thread over.
const RunChat = ({ runId, channel }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(resetChatFeed())
    return () => dispatch(resetChatFeed())
  }, [dispatch, runId])

  return (
    <BaltoGPTChat
      useMinimalUi
      channel={channel}
      callIds={[]}
      suggestions={SUGGESTED_QUESTIONS}
      disableAutoScroll
      disableAutoFocus
      onSubmit={(values, signal) => dispatch(searchRunWithBaltoGPT(runId, values, signal))}
    />
  )
}

// Explore = Understand. A descriptive breakdown of how the run's calls mapped to
// conversion opportunities. Shown one run at a time (aggregating deep breakdowns
// across runs is muddy), so under "All playbooks" we prompt to pick one.
export const ExploreTab = ({
  scopeInsights,
  scope,
  hasExpiredInsights,
  onOpenReason,
  onOpenInconclusive,
}) => {
  const [flashedIndex, setFlashedIndex] = useState(null)
  const dispatch = useDispatch()

  // No current run to chat over or export, so the whole tab gives way to the
  // explanation rather than a disabled toolbar over an empty breakdown. Checked
  // before "All": with nothing current there's no playbook left to pick.
  if (hasExpiredInsights) {
    return <EmptyState kind={EMPTY_STATE.EXPIRED} />
  }

  if (scope === SCOPE_ALL) {
    return (
      <p className="agentic-muted">
        Pick a single playbook above to see its call breakdown. The breakdown maps one run&apos;s
        calls into conversion opportunities, so it&apos;s shown one run at a time.
      </p>
    )
  }

  const runId = scopeInsights[0]?.pipelineRunId
  const channel = scopeInsights[0]?.channel
  const { opportunities, noOpportunity } = buildRunBreakdown(scopeInsights)

  const jumpToNoOpp = () => {
    const element = document.getElementById('opportunity-noopp')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Jump to an opportunity's card and flash it so it's obvious which one the
  // overview bar landed on. The flash starts when the smooth scroll actually
  // settles (scrollend) — not on click — so it lands as the card arrives even
  // after a long scroll; a fallback covers browsers/containers that don't emit
  // scrollend. If the card is already visible, flash immediately.
  const jumpToOpportunity = (name) => {
    const index = opportunities.findIndex((opportunity) => opportunity.name === name)
    const element = document.getElementById(`opportunity-${index}`)
    if (!element) {
      return
    }

    const flash = () => {
      setFlashedIndex(index)
      window.setTimeout(() => {
        setFlashedIndex((current) => (current === index ? null : current))
      }, FLASH_MS)
    }

    const rect = element.getBoundingClientRect()
    const alreadyInView = rect.top >= 0 && rect.bottom <= window.innerHeight
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })

    if (alreadyInView) {
      flash()
      return
    }

    let started = false
    const start = () => {
      if (started) {
        return
      }
      started = true
      window.removeEventListener('scrollend', start)
      flash()
    }
    window.addEventListener('scrollend', start)
    window.setTimeout(start, 1500)
  }

  return (
    <div className="explore-tab">
      <div className="explore-tab__ask">
        <div className="explore-tab__ask-bar">
          <span className="explore-tab__ask-title">Ask about these calls</span>
          <Button
            basic
            className="explore-tab__export"
            icon={<IconDownload />}
            disabled={!runId}
            onClick={() => dispatch(exportRunCallsCsv(runId))}
          >
            Export CSV
          </Button>
        </div>
        {runId && <RunChat runId={runId} channel={channel} />}
      </div>

      {isEmpty(opportunities) ? (
        <p className="agentic-muted">No conversion opportunities to break down for this run yet.</p>
      ) : (
        <>
          <OverviewBar
            opportunities={opportunities}
            noOpportunity={noOpportunity}
            onSelect={jumpToOpportunity}
            onSelectNoOpp={jumpToNoOpp}
          />
          <div className="explore-tab__opps">
            {opportunities.map((opportunity, index) => (
              <div
                key={opportunity.name}
                id={`opportunity-${index}`}
                className={classNames({ 'opportunity-flash': flashedIndex === index })}
              >
                <OpportunityCard
                  opportunity={opportunity}
                  hueIndex={index}
                  onOpenReason={onOpenReason}
                  onOpenInconclusive={() => onOpenInconclusive(opportunity)}
                />
              </div>
            ))}
          </div>
          <div className="explore-tab__divider">
            Not a conversion opportunity
            <span className="ln" />
          </div>
          <NoOppCard noOpportunity={noOpportunity} />
        </>
      )}
    </div>
  )
}
